import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, FileText, Loader2, Plus, RefreshCw, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ConfirmButton } from "@/components/ConfirmButton";
import { ContractFormDialog } from "@/components/contracts/ContractFormDialog";
import { StatoBadge, TIPO_COM } from "@/components/contracts/ContractsTable";
import { HoursTimeline } from "@/components/contracts/HoursTimeline";
import { api, formatApiError, formatDate } from "@/lib/api";

const openFile = async (c) => {
  const res = await api.get(`/contracts/${c.id}/file`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const nextDay = (iso) => { if (!iso) return ""; const d = new Date(iso + "T00:00:00"); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); };

export const prorogaFrom = (c) => ({
  tipo_comunicazione: "proroga", tipologia_contratto: c.tipologia_contratto, mansione: c.mansione, qualifica: c.qualifica,
  livello: c.livello, ccnl: c.ccnl, tipo_orario: c.tipo_orario, ore_settimanali: c.ore_settimanali ?? "",
  data_inizio: nextDay(c.data_fine), data_fine: "", luogo_lavoro: c.luogo_lavoro, note: "",
});

const ContractCard = ({ c, onEdit, onDelete, onProroga, onReparse }) => (
  <div className="rounded-lg border border-slate-200 p-4" data-testid={`contract-card-${c.id}`}>
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{c.tipo_documento}</span>
      {c.tipo_comunicazione && <span className="text-xs font-medium text-slate-700">{TIPO_COM[c.tipo_comunicazione]}</span>}
      <span className="ml-auto"><StatoBadge stato={c.stato} /></span>
    </div>
    <p className="mt-2 font-medium text-slate-900">{c.tipologia_contratto || "Contratto"}</p>
    <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600">
      <dt>Mansione</dt><dd className="text-slate-900">{c.mansione || "—"}</dd>
      <dt>Qualifica / livello</dt><dd className="text-slate-900">{[c.qualifica, c.livello].filter(Boolean).join(" · ") || "—"}</dd>
      <dt>Ore settimanali</dt><dd className="font-mono text-slate-900">{c.ore_settimanali ?? "—"}{c.tipo_orario ? ` · ${c.tipo_orario}` : ""}</dd>
      <dt>Periodo</dt><dd className="text-slate-900">{c.data_inizio ? formatDate(c.data_inizio, false) : "?"} → {c.data_fine ? formatDate(c.data_fine, false) : "indeterminato"}</dd>
      <dt>CCNL</dt><dd className="text-slate-900">{c.ccnl || "—"}</dd>
      {c.retribuzione && <><dt>Retribuzione</dt><dd className="text-slate-900">{c.retribuzione}</dd></>}
      {c.note && <><dt>Note</dt><dd className="text-slate-900">{c.note}</dd></>}
    </dl>
    <p className="mt-2 text-[11px] text-slate-400">
      {c.origine === "email" ? `Da email: ${c.email_subject || c.filename} (${formatDate(c.email_date, false)})` : c.filename ? `Caricato: ${c.filename}` : "Inserito manualmente"}
    </p>
    <div className="mt-3 flex gap-1">
      {c.has_file && <Button size="sm" variant="outline" onClick={() => openFile(c)} data-testid={`open-contract-file-${c.id}`}><FileText className="mr-1 h-3.5 w-3.5" /> Apri</Button>}
      {c.tipo_comunicazione !== "cessazione" && c.data_fine && (
        <Button size="sm" variant="outline" onClick={() => onProroga(c)} data-testid={`proroga-contract-${c.id}`}><CalendarPlus className="mr-1 h-3.5 w-3.5" /> Registra proroga</Button>
      )}
      <Button size="sm" variant="ghost" onClick={() => onEdit(c)} data-testid={`edit-contract-${c.id}`}>Modifica</Button>
      {c.has_file && <Button size="sm" variant="ghost" onClick={() => onReparse(c)} data-testid={`reparse-contract-${c.id}`}><RefreshCw className="mr-1 h-3.5 w-3.5" /> Rianalizza</Button>}
      <ConfirmButton testId={`delete-contract-${c.id}`} size="sm" title="Eliminare questo documento?" onConfirm={() => onDelete(c)}><Trash2 className="h-3.5 w-3.5" /></ConfirmButton>
    </div>
  </div>
);

export const EmployeeContractsSheet = ({ row, onClose, onChanged }) => {
  const [list, setList] = useState([]);
  const [dialog, setDialog] = useState({ open: false, contract: null, initial: null });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    if (!row) return;
    api.get("/contracts", { params: { employee_id: row.employee_id } }).then((r) => setList(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [row]);
  useEffect(load, [load]);

  const changed = () => { load(); onChanged(); };
  const current = row?.current || list[0] || null;
  const reparse = async (c) => {
    try {
      const { data } = await api.post(`/contracts/${c.id}/reparse`);
      toast.success(data.employee_id !== c.employee_id ? "Documento rianalizzato e riabbinato al dipendente corretto" : "Documento rianalizzato");
      changed();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (c) => {
    try { await api.delete(`/contracts/${c.id}`); toast.success("Documento eliminato"); changed(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("employee_id", row.employee_id);
    try {
      await api.post("/contracts/upload", fd);
      toast.success("Documento analizzato e salvato");
      changed();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  return (
    <Sheet open={!!row} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="employee-contracts-sheet">
        {row && (
          <>
            <SheetHeader>
              <SheetTitle>{row.cognome} {row.nome}</SheetTitle>
              <SheetDescription className="font-mono text-xs">{row.codice_fiscale || "Codice fiscale mancante"}</SheetDescription>
            </SheetHeader>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button size="sm" onClick={() => setDialog({ open: true, contract: null, initial: null })} data-testid="add-contract-manual-button"><Plus className="mr-1 h-4 w-4" /> Inserisci a mano</Button>
              {current && current.data_fine && current.tipo_comunicazione !== "cessazione" && (
                <Button size="sm" variant="secondary" onClick={() => setDialog({ open: true, contract: null, initial: prorogaFrom(current) })} data-testid="proroga-current-button"><CalendarPlus className="mr-1 h-4 w-4" /> Registra proroga</Button>
              )}
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-contract-button">
                {uploading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />} Carica PDF/DOCX
              </Button>
              <input ref={fileRef} type="file" accept=".pdf,.docx" className="hidden" onChange={upload} data-testid="upload-contract-input" />
            </div>
            <div className="mt-4 space-y-3">
              <HoursTimeline contracts={list} />
              {list.map((c) => <ContractCard key={c.id} c={c} onEdit={(x) => setDialog({ open: true, contract: x, initial: null })} onDelete={remove} onReparse={reparse} onProroga={(x) => setDialog({ open: true, contract: null, initial: prorogaFrom(x) })} />)}
              {list.length === 0 && <p className="py-8 text-center text-sm text-slate-500" data-testid="employee-contracts-empty">Nessun documento per questo dipendente. Inseriscilo a mano o carica il file.</p>}
            </div>
            <ContractFormDialog open={dialog.open} contract={dialog.contract} initial={dialog.initial} employeeId={row.employee_id}
              onOpenChange={(open) => setDialog((d) => ({ ...d, open }))} onSaved={changed} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
